export type OpenReviewAttempt = {
  conversationId: string;
  fenceId: string;
};

export type OpenReviewAttemptScheduler = (clear: () => void) => () => void;

const defaultScheduler: OpenReviewAttemptScheduler = (clear) => {
  const timer = setTimeout(clear, 0);
  return () => clearTimeout(timer);
};

// Browser reuse lives only while a review card is actually open. Closing the
// card must drop the fence, but React StrictMode remounts the same card in the
// same turn, so the drop is deferred until the current turn finishes.
export function createOpenReviewAttemptTracker(
  scheduleClear: OpenReviewAttemptScheduler = defaultScheduler,
) {
  let current: OpenReviewAttempt | null = null;
  let openConversationId: string | null = null;
  let pendingCloseConversationId: string | null = null;
  let cancelScheduledClear: (() => void) | null = null;

  const cancelPendingClear = () => {
    cancelScheduledClear?.();
    cancelScheduledClear = null;
  };

  const belongsToOpenOrClosingCard = (conversationId: string): boolean => (
    openConversationId === conversationId
    || pendingCloseConversationId === conversationId
  );

  return {
    get(): OpenReviewAttempt | null {
      return openConversationId && current?.conversationId === openConversationId
        ? current
        : null;
    },
    begin(conversationId: string): void {
      cancelPendingClear();
      pendingCloseConversationId = null;
      if (current?.conversationId !== conversationId) current = null;
      openConversationId = conversationId;
    },
    confirm(conversationId: string, fenceId: string | null | undefined): void {
      if (!belongsToOpenOrClosingCard(conversationId)) return;
      current = fenceId ? { conversationId, fenceId } : null;
    },
    reuseFor(conversationId: string | undefined): string | undefined {
      return conversationId && openConversationId === conversationId
        && current?.conversationId === conversationId
        ? current.fenceId
        : undefined;
    },
    end(conversationId?: string): void {
      const closingConversationId = conversationId ?? openConversationId;
      if (!closingConversationId) return;
      if (openConversationId && openConversationId !== closingConversationId) return;
      openConversationId = null;
      pendingCloseConversationId = closingConversationId;
      cancelPendingClear();
      cancelScheduledClear = scheduleClear(() => {
        if (pendingCloseConversationId === closingConversationId) {
          current = null;
          pendingCloseConversationId = null;
        }
        cancelScheduledClear = null;
      });
    },
    clear(): void {
      cancelPendingClear();
      current = null;
      openConversationId = null;
      pendingCloseConversationId = null;
    },
  };
}

export type OpenReviewAttemptTracker = ReturnType<typeof createOpenReviewAttemptTracker>;
